import { PARAMETER_CONFIG } from '../core/parameter-config.js';
import {
	slugify,
	parseSubmissionUrl,
	getFormParamNames,
	hasRelevantParams,
	parseGenericParams,
	parseSpecialtyParams,
	orderSearchParams,
	buildFilterParams,
	assembleSearchUrl,
} from '../utils/url-logic.js';
import {
	readFormSubmissionUrlAttribute,
	readOmitStateInsurancePath,
	readSelections,
	readSlugFromSelection,
	findMatchingCheckboxes,
	applyCheckboxSelection,
	findRadiosToSelect,
	applyRadioSelection,
} from '../utils/url-dom.js';

export class URLManager {
	constructor(forms, callbacks) {
		this.forms = forms;
		this.callbacks = callbacks;

		// Store generic parameters that aren't form fields
		this.genericParams = new Map();

		this.parameterConfig = PARAMETER_CONFIG;

		// Precompute the set of known form parameter names for classification.
		this.formParamNames = getFormParamNames(PARAMETER_CONFIG);
	}

	shouldOmitStateInsurancePath() {
		return readOmitStateInsurancePath(this.forms[0]);
	}

	/**
	 * Parses the form submission URL attribute into path-only and optional query string.
	 * Supports values like "/eap/amazon/find/" or "/eap/amazon/find/?utm_campaign=amazon-eap".
	 *
	 * @return {{ pathOnly: string, pathSegments: string[], queryString: string }} Raw path (preserving any trailing slash and absolute/external URLs), path segments, and raw query string (including leading '?' or '')
	 */
	getFormSubmissionUrlParsed() {
		return parseSubmissionUrl(readFormSubmissionUrlAttribute(this.forms[0]));
	}

	populateFromUrlParams() {
		const urlParams = new URLSearchParams(window.location.search);

		// Parse and store generic parameters first (for forwarding)
		this.genericParams = parseGenericParams(
			urlParams,
			this.formParamNames,
			this.parameterConfig.specialty,
		);

		// If there are no parameters relevant to the form, return
		if (!hasRelevantParams(urlParams, this.parameterConfig)) {
			return;
		}

		const populationResults = [];

		Object.entries(this.parameterConfig).forEach(([key, config]) => {
			if (config.singleSelect) {
				const value = urlParams.get(config.paramName);
				if (value) {
					const result = this.populateDropdownFromParam(config.dropdownType, [
						value,
					]);
					if (result > 0) {
						populationResults.push({ type: key, count: result });
					}
				}
			} else {
				// Handle specialty parameters (multi-select)
				const values = parseSpecialtyParams(
					urlParams,
					config,
					window.location.search,
				);
				if (values.length > 0) {
					const result = this.populateDropdownFromParam(
						config.dropdownType,
						values,
					);
					if (result > 0) {
						populationResults.push({ type: key, count: result });
					}
				}
			}
		});

		if (populationResults.length > 0) {
			this.triggerMultiFormSync();
		}
	}

	populateDropdownFromParam(dropdownType, values) {
		// Handle radio inputs for tabbed fields
		if (dropdownType === 'type-of-care-options') {
			return this.populateRadioFromParam(dropdownType, values);
		}

		const matching = findMatchingCheckboxes(dropdownType, values);

		if (matching.length > 0) {
			requestAnimationFrame(() => {
				matching.forEach((checkbox) => this.processCheckboxUpdate(checkbox));
			});
		}

		return matching.length;
	}

	/**
	 * Populates radio inputs from URL parameters for tabbed fields
	 * @param {string}          dropdownType - The dropdown type (e.g., 'type-of-care-options')
	 * @param {string|string[]} values       - The values to select
	 * @return {number} Number of radio inputs populated
	 */
	populateRadioFromParam(dropdownType, values) {
		const value = Array.isArray(values) ? values[0] : values;
		if (!value) return 0;

		const radios = findRadiosToSelect(dropdownType, value);
		radios.forEach((radio) => this.processRadioUpdate(radio));

		return radios.length;
	}

	processCheckboxUpdate(checkbox) {
		applyCheckboxSelection(checkbox);

		this.callbacks.updateDropdownLabel(checkbox);
		if (this.callbacks.syncCheckboxToAllForms) {
			this.callbacks.syncCheckboxToAllForms(checkbox);
		}
	}

	/**
	 * Processes radio input updates for tabbed fields
	 * @param {HTMLInputElement} radio - The radio input to process
	 */
	processRadioUpdate(radio) {
		if (!applyRadioSelection(radio)) return;

		// Sync radio selection to all forms
		if (this.callbacks.syncRadioToAllForms) {
			this.callbacks.syncRadioToAllForms(radio);
		}
	}

	triggerMultiFormSync() {
		if (this.callbacks.triggerCrossFiltering) {
			this.callbacks.triggerCrossFiltering();
		}
	}

	/**
	 * Builds URLSearchParams from form checkbox and radio selections.
	 *
	 * Side-effect free: it only reads the form and returns params. The filter-history
	 * write lives in commitFilterHistory so callers control when that side effect fires.
	 *
	 * @param {HTMLFormElement}         form         - The form to build the URL parameters from
	 * @param {Object<string,string[]>} [selections] - Pre-read selections; defaults to reading the form. Pass this to avoid re-reading the DOM when the caller already has them.
	 * @return {URLSearchParams} The ordered search params, including forwarded generic params.
	 */
	buildSearchParams(form, selections = readSelections(form)) {
		// Build the ordered search params (form params)
		const searchParams = orderSearchParams(selections);

		// Add generic parameters that were captured from the initial URL
		for (const [key, value] of this.genericParams.entries()) {
			try {
				searchParams.append(key, value);
			} catch (error) {
				// Skip invalid parameters that can't be appended
				// eslint-disable-next-line no-console
				console.warn('Skipping invalid generic parameter:', key, error.message);
			}
		}

		return searchParams;
	}

	/**
	 * Writes the current form selections to browser history as filter params.
	 *
	 * Kept separate from buildSearchParams so param construction stays side-effect free;
	 * call this at submit time, before redirecting.
	 *
	 * @param {HTMLFormElement}         form         - The form to read selections from.
	 * @param {Object<string,string[]>} [selections] - Pre-read selections; defaults to reading the form. Pass this to avoid re-reading the DOM when the caller already has them.
	 */
	commitFilterHistory(form, selections = readSelections(form)) {
		const filterParams = buildFilterParams(selections);

		history.replaceState(
			null,
			'',
			window.location.pathname + '?' + filterParams.toString(),
		);
	}

	/**
	 * Constructs the final search URL from URLSearchParams.
	 *
	 * @param {URLSearchParams} searchParams - The URL search parameters to build the URL from
	 */
	buildSearchUrl(searchParams) {
		const stateValue = searchParams.get('state');
		const insuranceValue = searchParams.get('insurance');
		const { pathOnly, pathSegments, queryString } =
			this.getFormSubmissionUrlParsed();
		const omitStateInsurancePath = this.shouldOmitStateInsurancePath();

		// Preserve the configured path's trailing slash — regardless of whether we
		// append state/insurance segments — so we submit to WordPress's canonical
		// URL. Otherwise the server issues an add-trailing-slash redirect, which the
		// full-page cache can store with a stale query string frozen into it.
		const hasTrailingSlash = pathOnly.endsWith('/');

		// Resolve a slug for each selected value (a null slug means "not selected — don't
		// add to the path"). Whether these slugs actually get appended is decided solely by
		// assembleSearchUrl via omitStateInsurancePath, so the omit rule has one owner.
		let stateSlug = null;
		let insuranceSlug = null;
		if (stateValue) {
			stateSlug = readSlugFromSelection('states-options', stateValue, slugify);
		}
		if (insuranceValue) {
			insuranceSlug = readSlugFromSelection(
				'payors-options',
				insuranceValue,
				slugify,
			);
		}

		return assembleSearchUrl({
			hasTrailingSlash,
			pathSegments,
			queryString,
			searchParams,
			stateSlug,
			insuranceSlug,
			omitStateInsurancePath,
		});
	}
}
